import React, { useRef, useState } from "react";
import { Upload, X, FileText, Image as ImageIcon } from "lucide-react";
import { api, formatApiError, fileUrl } from "@/lib/api";
import { toast } from "sonner";

/**
 * FileUploader
 * - onChange(files: Array<{id, name, url}>): called whenever files list updates.
 * - accept: pass-through accept attribute
 * - multiple: bool
 * - testId
 */
export default function FileUploader({ onChange, accept = "image/*,application/pdf", multiple = true, testId = "file-uploader" }) {
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const inputRef = useRef(null);

  const handleSelect = async (e) => {
    const chosen = Array.from(e.target.files || []);
    if (!chosen.length) return;
    setBusy(true);
    const next = [...files];
    for (const f of chosen) {
      const fd = new FormData();
      fd.append("file", f);
      try {
        const { data } = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
        next.push(data);
      } catch (err) {
        toast.error(formatApiError(err.response?.data?.detail) || `Couldn't upload ${f.name}`);
      }
    }
    setFiles(next);
    onChange && onChange(next);
    if (inputRef.current) inputRef.current.value = "";
    setBusy(false);
  };

  const remove = (id) => {
    const next = files.filter((f) => f.id !== id);
    setFiles(next);
    onChange && onChange(next);
  };

  return (
    <div data-testid={testId}>
      <button type="button" onClick={() => inputRef.current?.click()} disabled={busy}
        className="btn-ghost-navy !py-2 !text-[10px] disabled:opacity-50" data-testid={`${testId}-trigger`}>
        <Upload size={14} /> {busy ? "Uploading…" : "Attach photo / PDF"}
      </button>
      <input ref={inputRef} type="file" accept={accept} multiple={multiple} onChange={handleSelect} className="hidden" data-testid={`${testId}-input`} />
      {files.length > 0 && (
        <ul className="mt-3 space-y-2">
          {files.map((f) => {
            const isPdf = (f.name || "").toLowerCase().endsWith(".pdf");
            return (
              <li key={f.id} className="flex items-center justify-between border border-brand-border px-3 py-2 bg-white">
                <a href={fileUrl(f.url)} target="_blank" rel="noreferrer" className="flex items-center gap-2 text-xs text-brand-navy truncate hover:text-brand-cyan">
                  {isPdf ? <FileText size={14} /> : <ImageIcon size={14} />} {f.name}
                </a>
                <button type="button" onClick={() => remove(f.id)} className="text-brand-slate/60 hover:text-red-500" aria-label="Remove">
                  <X size={14} />
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
